import type { ReactNode } from 'react';

type TimestampVariant = 'message' | 'activity' | 'exact';

type TimestampProps = {
  value: string | Date | null | undefined;
  variant?: TimestampVariant;
  relativeTo?: string | Date | null;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
  now?: Date;
};

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

const activityTimeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

const weekdayTimeFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

const monthDayTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

const longDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

const exactFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short'
});

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatDelta(ms: number) {
  const abs = Math.abs(ms);
  const sign = ms >= 0 ? '+' : '-';
  if (abs < 1000) {
    return `${sign}${abs}ms`;
  }
  if (abs < 60_000) {
    return `${sign}${Math.round(abs / 1000)}s`;
  }
  if (abs < 3_600_000) {
    return `${sign}${Math.round(abs / 60_000)}m`;
  }
  return `${sign}${Math.round(abs / 3_600_000)}h`;
}

export function formatTimestampLabel(
  value: string | Date | null | undefined,
  variant: TimestampVariant = 'message',
  now: Date = new Date()
) {
  const date = parseDate(value);
  if (!date) {
    return '';
  }

  if (variant === 'activity') {
    return activityTimeFormatter.format(date);
  }

  if (variant === 'exact') {
    return exactFormatter.format(date);
  }

  const diffMs = now.getTime() - date.getTime();
  if (diffMs >= 0 && diffMs < 60_000) {
    return 'now';
  }
  if (diffMs >= 0 && diffMs < 3_600_000) {
    return `${Math.max(1, Math.round(diffMs / 60_000))}m ago`;
  }

  const dayDiff = Math.floor(
    (startOfLocalDay(now) - startOfLocalDay(date)) / 86_400_000
  );
  if (dayDiff === 0) {
    return timeFormatter.format(date);
  }
  if (dayDiff === 1) {
    return `Yesterday ${timeFormatter.format(date)}`;
  }
  if (dayDiff > 1 && dayDiff <= 6) {
    return weekdayTimeFormatter.format(date);
  }
  if (date.getFullYear() === now.getFullYear()) {
    return monthDayTimeFormatter.format(date);
  }
  return longDateTimeFormatter.format(date);
}

export function Timestamp({
  value,
  variant = 'message',
  relativeTo,
  prefix,
  suffix,
  className,
  now
}: TimestampProps) {
  const date = parseDate(value);
  if (!date) {
    return null;
  }

  const iso = date.toISOString();
  const exact = exactFormatter.format(date);
  const relativeDate = parseDate(relativeTo);
  const delta = variant === 'activity' && relativeDate
    ? formatDelta(date.getTime() - relativeDate.getTime())
    : null;
  const label = formatTimestampLabel(date, variant, now);
  const title = `${exact}\n${iso}\nClick to copy ISO datetime`;

  const copyIso = () => {
    void navigator.clipboard?.writeText(iso).catch(() => undefined);
  };

  return (
    <span className={['timestamp-wrap', className].filter(Boolean).join(' ')}>
      {prefix}
      <button
        type="button"
        className={`timestamp timestamp--${variant}`}
        title={title}
        aria-label={`${exact}. Click to copy ISO datetime.`}
        onClick={copyIso}
      >
        {label}
      </button>
      {delta && (
        <span className="timestamp-delta" title={`Delta from previous event: ${delta}`}>
          {delta}
        </span>
      )}
      {suffix}
    </span>
  );
}
